import React, { useState, useEffect } from "react";
import {
    Box,
    Drawer,
    Typography,
    Divider,
    Slider,
    FormControlLabel,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useMediaQuery,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ref, get } from "firebase/database";
import { rtdb } from "../services/firebase";

export default function FiltrosSidebar({ 
    mobile = false, 
    open, 
    onClose,
    produtos = [],
    onFiltersChange 
}) {
    const [categorias, setCategorias] = useState([]);
    const [precoRange, setPrecoRange] = useState([0, 500]);
    const [preco, setPreco] = useState([0, 500]);
    const [categoriasSelecionadas, setCategoriasSelecionadas] = useState([]);
    const [promocoes, setPromocoes] = useState(false);
    const [emEstoque, setEmEstoque] = useState(false);

    // Carregar categorias do Firebase
    useEffect(() => {
        async function carregarCategorias() {
            try {
                const appSnap = await get(ref(rtdb, "app/category"));
                if (appSnap.exists()) {
                    const cats = appSnap.val();
                    setCategorias(Array.isArray(cats) ? cats : []);
                }
            } catch (err) {
                console.error("Erro ao carregar categorias:", err);
            }
        }
        carregarCategorias();
    }, []);

    // Função auxiliar para converter preço string para número
    function converterPreco(precoStr) {
        if (!precoStr) return 0;
        const limpo = precoStr.toString().replace(/[^\d,.]/g, "");
        const numero = parseFloat(limpo.replace(",", "."));
        return isNaN(numero) ? 0 : numero;
    }

    // Calcular preço máximo baseado nos produtos
    useEffect(() => {
        if (produtos.length === 0) return;

        const precos = produtos.map((p) => {
            const precoAtual = p.precos?.isPromo && p.precos?.promo 
                ? converterPreco(p.precos.promo)
                : converterPreco(p.precos?.normal);
            return precoAtual;
        });

        const maxPreco = Math.max(...precos, 0);
        const maxPrecoArredondado = Math.ceil(maxPreco / 50) * 50; // Arredonda para cima em múltiplos de 50

        setPrecoRange([0, maxPrecoArredondado || 500]);
        setPreco([0, maxPrecoArredondado || 500]);
    }, [produtos]);

    // Notificar mudanças nos filtros
    useEffect(() => {
        if (onFiltersChange) {
            onFiltersChange({
                preco,
                categorias: categoriasSelecionadas,
                promocoes,
                emEstoque,
            });
        }
    }, [preco, categoriasSelecionadas, promocoes, emEstoque, onFiltersChange]);

    const handleCategoriaChange = (categoria) => {
        setCategoriasSelecionadas((prev) =>
            prev.includes(categoria)
                ? prev.filter((c) => c !== categoria)
                : [...prev, categoria]
        );
    };

    // Breakpoints reais
    const xxl = useMediaQuery("(min-width:1600px)"); // telas enormes
    const xl = useMediaQuery("(min-width:1400px)");
    const lg = useMediaQuery("(min-width:1200px)");
    const md = useMediaQuery("(min-width:992px)");
    const sm = useMediaQuery("(min-width:900px)");
    const isMobile = useMediaQuery("(max-width:900px)");

    // Cálculo profissional de largura
    const sidebarWidth = isMobile
        ? "85vw"
        : xxl
            ? 500
            : xl
                ? 460
                : lg
                    ? 420
                    : md
                        ? 350
                        : sm
                            ? 300
                            : 260;

    const SidebarContent = (
        <Box
            sx={{
                width: sidebarWidth,
                p: 2.5,
                background: "#fff",
                height: "100vh",
                overflowY: "auto",
                borderRight: isMobile ? "none" : "1px solid #e5e5e5",

                "&::-webkit-scrollbar": { width: 5 },
                "&::-webkit-scrollbar-thumb": {
                    background: "#d0d0d0",
                    borderRadius: 10,
                },
            }}
        >
            <Typography
                sx={{
                    fontWeight: 800,
                    fontSize: 20,
                    mb: 1.5,
                    color: "var(--azul)",
                }}
            >
                Filtros
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* ========== PREÇO ========== */}
            <Accordion defaultExpanded sx={{ boxShadow: "none" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>Preço</Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Slider
                        value={preco}
                        onChange={(e, val) => setPreco(val)}
                        valueLabelDisplay="auto"
                        min={precoRange[0]}
                        max={precoRange[1]}
                        sx={{
                            color: "var(--azul)",
                            "& .MuiSlider-thumb": {
                                width: 16,
                                height: 16,
                            },
                        }}
                    />

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography fontSize={12}>
                            R$ {Math.round(preco[0]).toLocaleString("pt-BR")}
                        </Typography>
                        <Typography fontSize={12}>
                            R$ {Math.round(preco[1]).toLocaleString("pt-BR")}
                        </Typography>
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* ========== CATEGORIAS ========== */}
            <Accordion defaultExpanded sx={{ boxShadow: "none", mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>Categorias</Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ pl: 2 }}>
                    {categorias.length === 0 ? (
                        <Typography fontSize={14} color="#999">
                            Carregando categorias...
                        </Typography>
                    ) : (
                        categorias.map((cat) => (
                            <FormControlLabel
                                key={cat}
                                sx={{ m: 0, mb: 1, display: "block" }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={categoriasSelecionadas.includes(cat)}
                                        onChange={() => handleCategoriaChange(cat)}
                                        sx={{
                                            color: "var(--azul)",
                                            "&.Mui-checked": { color: "var(--azul)" },
                                        }}
                                    />
                                }
                                label={cat}
                            />
                        ))
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ========== FRETE ========== */}
            <Accordion defaultExpanded sx={{ boxShadow: "none", mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>Frete</Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ pl: 2 }}>
                    {["Frete grátis", "Envio rápido"].map((item) => (
                        <FormControlLabel
                            key={item}
                            sx={{ m: 0, mb: 1 }}
                            control={
                                <Checkbox
                                    size="small"
                                    sx={{
                                        color: "var(--azul)",
                                        "&.Mui-checked": { color: "var(--azul)" },
                                    }}
                                />
                            }
                            label={item}
                        />
                    ))}
                </AccordionDetails>
            </Accordion>

            {/* ========== MAIS FILTROS ========== */}
            <Accordion sx={{ boxShadow: "none", mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>Mais filtros</Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ pl: 2 }}>
                    <FormControlLabel
                        sx={{ m: 0, mb: 1, display: "block" }}
                        control={
                            <Checkbox
                                size="small"
                                checked={promocoes}
                                onChange={(e) => setPromocoes(e.target.checked)}
                                sx={{
                                    color: "var(--azul)",
                                    "&.Mui-checked": { color: "var(--azul)" },
                                }}
                            />
                        }
                        label="Promoções"
                    />
                    <FormControlLabel
                        sx={{ m: 0, mb: 1, display: "block" }}
                        control={
                            <Checkbox
                                size="small"
                                checked={emEstoque}
                                onChange={(e) => setEmEstoque(e.target.checked)}
                                sx={{
                                    color: "var(--azul)",
                                    "&.Mui-checked": { color: "var(--azul)" },
                                }}
                            />
                        }
                        label="Em estoque"
                    />
                </AccordionDetails>
            </Accordion>
        </Box>
    );

    return (
        <>
            {/* MOBILE → Drawer */}
            {mobile ? (
                <Drawer anchor="left" open={open} onClose={onClose}>
                    {SidebarContent}
                </Drawer>
            ) : (
                SidebarContent
            )}
        </>
    );
}
